export const MAX_WHEEL_ORDER = 5;
export const WHEEL_NODE_WIDTH = 240;
export const WHEEL_NODE_HEIGHT = 92;
export function wheelNodeSize(order = 0) {
  const level = Math.max(0, Math.min(MAX_WHEEL_ORDER, Number(order) || 0));
  return { width: [260, 240, 224, 208, 192, 176][level], height: [100, 92, 86, 80, 74, 68][level] };
}
export function wrapFuturesWheelDomainLabel(value, limit = 22) {
  const lines = [];
  let line = "";
  for (const word of String(value || "Domain").split(/\s+/)) {
    const candidate = `${line} ${word}`.trim();
    if (candidate.length > limit && line) { lines.push(line); line = word; }
    else line = candidate;
  }
  if (line) lines.push(line);
  if (lines.length > 3) lines[2] = `${lines[2].slice(0, Math.max(1, limit - 1))}…`;
  return lines.slice(0, 3);
}
export function futuresWheelDomainLabelLayout(sectors = [], nodeCount = 0, sectorOuterRadius = 0, obstacles = []) {
  if (!sectors.length) return null;
  const count = sectors.length;
  const fontSize = Math.min(count > 10 ? 44 : 64, Math.max(count > 20 ? 18 : 28, Math.sqrt(Math.max(1, nodeCount)) * 4.2));
  const lineLimit = count > 20 ? 18 : 22;
  const linesById = new Map(sectors.map((sector) => [String(sector.id), wrapFuturesWheelDomainLabel(sector.label, lineLimit)]));
  const labelsById = new Map();
  const boxes = [...obstacles];
  for (const sector of sectors) {
    const lines = linesById.get(String(sector.id));
    const width = Math.max(...lines.map((line) => line.length), 1) * fontSize * 0.62;
    const height = lines.length * fontSize * 1.1 + 20;
    const angle = sector.centerAngle ?? (sector.startAngle + sector.endAngle) / 2;
    const ux = Math.cos(angle);
    const uy = Math.sin(angle);
    const radialHalfExtent = Math.abs(ux) * width / 2 + Math.abs(uy) * height / 2;
    let radius = (sector.outerRadius ?? sectorOuterRadius) + radialHalfExtent + 24;
    let label;
    for (let attempt = 0; attempt < 512; attempt += 1) {
      label = { x: ux * radius, y: uy * radius, radius, width, height, lines, fontSize };
      const collides = boxes.some((box) => Math.abs(label.x - box.x) < (width + box.width) / 2 + 16 && Math.abs(label.y - box.y) < (height + box.height) / 2 + 16);
      if (!collides) break;
      radius += Math.max(24, fontSize);
    }
    labelsById.set(String(sector.id), label);
    boxes.push(label);
  }
  const labels = [...labelsById.values()];
  const labelWidth = Math.max(...labels.map((label) => label.width));
  const labelHeight = Math.max(...labels.map((label) => label.height));
  return { radius: Math.max(...labels.map((label) => label.radius)), fontSize, linesById, labelsById, maxLineWidth: labelWidth, labelWidth, labelHeight, halfExtent: Math.hypot(labelWidth / 2, labelHeight / 2) };
}
export function futuresWheelDomainOuterRadius(layout, sectorId = null) {
  if (sectorId != null) {
    const sectorRadius = layout.domainSectors?.find((sector) => sector.id === String(sectorId))?.outerRadius;
    if (Number.isFinite(sectorRadius)) return sectorRadius;
  }
  const sectorRadii = (layout.domainSectors || []).map((sector) => sector.outerRadius).filter(Number.isFinite);
  if (sectorRadii.length) return Math.max(...sectorRadii);
  const order = Math.max(0, (layout.radii || []).length - 1);
  if (order === 0) return 0;
  const size = wheelNodeSize(order);
  const nodeRadius = layout.outerRadii?.[order] ?? layout.outerRadius ?? layout.radii[order] ?? 0;
  return nodeRadius + Math.hypot(size.width / 2, size.height / 2) + NODE_GAP / 2;
}

/** The editor and every artwork export must draw the same angular region. */
export function futuresWheelDomainPath(layout, sector, offsetY = 0) {
  const radius = futuresWheelDomainOuterRadius(layout, sector.id);
  const cx = layout.centerX;
  const cy = layout.centerY + offsetY;
  const span = sector.endAngle - sector.startAngle;
  if (!(radius > 0) || !(span > 0)) return "";
  if (span >= Math.PI * 2 - 1e-8) {
    return `M ${cx} ${cy - radius} A ${radius} ${radius} 0 1 1 ${cx} ${cy + radius} A ${radius} ${radius} 0 1 1 ${cx} ${cy - radius} Z`;
  }
  const sx = cx + Math.cos(sector.startAngle) * radius;
  const sy = cy + Math.sin(sector.startAngle) * radius;
  const ex = cx + Math.cos(sector.endAngle) * radius;
  const ey = cy + Math.sin(sector.endAngle) * radius;
  return `M ${cx} ${cy} L ${sx} ${sy} A ${radius} ${radius} 0 ${span > Math.PI ? 1 : 0} 1 ${ex} ${ey} Z`;
}
const NODE_GAP = 48;
const ORDER_GAP = 96;
const CENTRAL_ORDER_GAP = 64;
const PADDING = 88;

/**
 * One radius per causal order; one immutable angular interval per branch.
 * Spacing grows only the crowded ring. Never rotate an order independently:
 * doing so moves descendants away from their parents and assigned domains.
 */
export function layoutFuturesWheel(nodes, edges) {
  const tau = Math.PI * 2;
  const byId = new Map(nodes.map((node) => [String(node.id), node]));
  const rank = new Map(nodes.map((node, index) => [String(node.id), index]));
  const orderOf = (id) => Math.max(0, Math.min(MAX_WHEEL_ORDER, Number(byId.get(id)?.data?.semantic_data?.futures_wheel?.order) || 0));
  const root = nodes.find((node) => node.data?.semantic_data?.futures_wheel?.order === 0);
  if (!root) return { positions: new Map(), width: 640, height: 560, centerX: 320, centerY: 280, radii: [0], domainSectors: [], domainLabelLayout: null, branchSectors: new Map() };
  const rootId = String(root.id);
  const children = new Map();
  for (const edge of edges) {
    const source = String(edge.source);
    const target = String(edge.target);
    if (!byId.has(source) || !byId.has(target) || orderOf(target) !== orderOf(source) + 1) continue;
    const siblings = children.get(source) || [];
    if (!siblings.includes(target)) siblings.push(target);
    children.set(source, siblings);
  }
  for (const siblings of children.values()) siblings.sort((a, b) => rank.get(a) - rank.get(b));
  const rootChildren = children.get(rootId) || [];
  const maxOrder = Math.max(0, ...nodes.map((node) => orderOf(String(node.id))));
  const layers = Array.from({ length: maxOrder + 1 }, () => []);
  const branchSectors = new Map();
  const domainByNode = new Map();
  const metrics = new Map();
  const measureBranch = (id) => {
    if (metrics.has(id)) return metrics.get(id);
    const descendants = (children.get(id) || []).map(measureBranch);
    const value = {
      items: 1 + descendants.reduce((sum, child) => sum + child.items, 0),
      leaves: Math.max(1, descendants.reduce((sum, child) => sum + child.leaves, 0)),
    };
    metrics.set(id, value);
    return value;
  };
  measureBranch(rootId);
  const placeChildren = (parentId, startAngle, endAngle, domainId = null) => {
    const siblings = children.get(parentId) || [];
    const totalWeight = siblings.reduce((sum, id) => sum + measureBranch(id).leaves, 0);
    let cursor = startAngle;
    for (const id of siblings) {
      const share = parentId === rootId ? 0.4 / siblings.length + 0.6 * measureBranch(id).leaves / totalWeight : measureBranch(id).leaves / totalWeight;
      const span = (endAngle - startAngle) * share;
      const interval = { startAngle: cursor, endAngle: cursor + span };
      branchSectors.set(id, interval);
      domainByNode.set(id, domainId);
      layers[orderOf(id)].push({ nodeId: id, angle: cursor + span / 2 });
      placeChildren(id, cursor, cursor + span, domainId);
      cursor += span;
    }
  };
  const configuredDomains = root.data?.semantic_data?.futures_wheel?.domains;
  let domainSectors = [];
  if (Array.isArray(configuredDomains) && configuredDomains.length && rootChildren.length) {
    const configuredIds = new Set(configuredDomains.map((domain) => String(domain.id)));
    const domainIdFor = (id) => {
      const configured = String(byId.get(id)?.data?.semantic_data?.futures_wheel?.domain_id || '');
      return configuredIds.has(configured) ? configured : '__unassigned__';
    };
    const domains = [...configuredDomains];
    if (rootChildren.some((id) => domainIdFor(id) === '__unassigned__')) domains.push({ id: '__unassigned__', label: 'Unassigned' });
    const populated = domains.map((domain) => {
      const firstLevelIds = rootChildren.filter((id) => domainIdFor(id) === String(domain.id));
      return { ...domain, id: String(domain.id), firstLevelIds, itemCount: firstLevelIds.reduce((sum, id) => sum + measureBranch(id).items, 0) };
    }).filter((domain) => domain.itemCount > 0);
    const totalItems = populated.reduce((sum, domain) => sum + domain.itemCount, 0);
    // A tiny domain still needs room for its direct consequence cards near the
    // center. Reserve that capacity, then share the rest by content volume.
    const minimumPerPrimary = Math.min(0.5, tau * 0.6 / rootChildren.length);
    const remainingAngle = tau - minimumPerPrimary * rootChildren.length;
    let cursor = -Math.PI / 2;
    domainSectors = populated.map((domain) => {
      const allocatedAngle = minimumPerPrimary * domain.firstLevelIds.length + remainingAngle * domain.itemCount / totalItems;
      const gap = populated.length === 1 ? 0 : Math.min(0.035, allocatedAngle * 0.045);
      const sector = { id: domain.id, label: String(domain.label || 'Domain'), itemCount: domain.itemCount, allocatedAngle, startAngle: cursor + gap, endAngle: cursor + allocatedAngle - gap, centerAngle: cursor + allocatedAngle / 2 };
      cursor += allocatedAngle;
      const totalWeight = domain.firstLevelIds.reduce((sum, id) => sum + measureBranch(id).leaves, 0);
      let branchCursor = sector.startAngle;
      for (const id of domain.firstLevelIds) {
        const share = 0.4 / domain.firstLevelIds.length + 0.6 * measureBranch(id).leaves / totalWeight;
        const span = (sector.endAngle - sector.startAngle) * share;
        branchSectors.set(id, { startAngle: branchCursor, endAngle: branchCursor + span });
        domainByNode.set(id, sector.id);
        layers[1].push({ nodeId: id, angle: branchCursor + span / 2 });
        placeChildren(id, branchCursor, branchCursor + span, sector.id);
        branchCursor += span;
      }
      return sector;
    });
  } else if (rootChildren.length === 1) {
    placeChildren(rootId, -Math.PI / 2 - Math.PI / 3, -Math.PI / 2 + Math.PI / 3);
  } else {
    placeChildren(rootId, -Math.PI / 2, Math.PI * 1.5);
  }

  const radii = [0];
  for (let order = 1; order <= maxOrder; order += 1) {
    const card = wheelNodeSize(order);
    const previous = wheelNodeSize(order - 1);
    // Horizontal cards need room for a visible causal line and its arrowhead.
    // Ring capacity still grows automatically when a crowded layer needs more.
    const ringGap = order === 1 ? CENTRAL_ORDER_GAP : ORDER_GAP;
    const radialClearance = Math.max((previous.width + card.width) / 2 + ringGap, (Math.hypot(previous.width, previous.height) + Math.hypot(card.width, card.height)) / 2 + 2);
    let radius = radii[order - 1] + radialClearance;
    const layer = layers[order];
    const gap = 24;
    // Exact Cartesian card clearance is much tighter than reserving a full
    // card diagonal around the entire circumference, especially at the sides.
    for (let index = 0; index < layer.length; index += 1) {
      for (let next = index + 1; next < layer.length; next += 1) {
        const dx = Math.abs(Math.cos(layer[index].angle) - Math.cos(layer[next].angle));
        const dy = Math.abs(Math.sin(layer[index].angle) - Math.sin(layer[next].angle));
        radius = Math.max(radius, Math.min((card.width + gap) / Math.max(dx, 1e-12), (card.height + gap) / Math.max(dy, 1e-12)));
      }
    }
    // Keep the complete card, rather than just its center, inside the domain.
    const sectorsById = new Map(domainSectors.map((sector) => [sector.id, sector]));
    for (const entry of layer) {
      const sector = sectorsById.get(domainByNode.get(entry.nodeId));
      if (!sector || sector.endAngle - sector.startAngle >= tau - 1e-8) continue;
      for (const [boundary, distance] of [[sector.startAngle, entry.angle - sector.startAngle], [sector.endAngle, sector.endAngle - entry.angle]]) {
        if (distance >= Math.PI / 2) continue;
        const normalHalfExtent = Math.abs(Math.sin(boundary)) * card.width / 2 + Math.abs(Math.cos(boundary)) * card.height / 2;
        radius = Math.max(radius, (normalHalfExtent + 12) / Math.sin(Math.max(1e-8, distance)));
      }
    }
    radii[order] = radius + 1e-7;
  }

  const relativePositions = new Map([[rootId, { x: 0, y: 0 }]]);
  for (let order = 1; order <= maxOrder; order += 1) {
    for (const entry of layers[order]) relativePositions.set(entry.nodeId, { x: Math.cos(entry.angle) * radii[order], y: Math.sin(entry.angle) * radii[order] });
  }
  domainSectors = domainSectors.map((sector) => {
    const deepestOrder = Math.max(0, ...nodes.filter((node) => domainByNode.get(String(node.id)) === sector.id).map((node) => orderOf(String(node.id))));
    const card = wheelNodeSize(deepestOrder);
    return { ...sector, deepestOrder, outerRadius: radii[deepestOrder] + Math.hypot(card.width, card.height) / 2 + NODE_GAP / 2 };
  });
  const outerRadius = radii[maxOrder] || 0;
  const obstacles = nodes.flatMap((node) => {
    const point = relativePositions.get(String(node.id));
    if (!point) return [];
    const size = wheelNodeSize(orderOf(String(node.id)));
    return [{ ...point, ...size }];
  });
  const domainLabelLayout = futuresWheelDomainLabelLayout(domainSectors, relativePositions.size, futuresWheelDomainOuterRadius({ radii, domainSectors }), obstacles);
  const extent = Math.max(outerRadius + WHEEL_NODE_WIDTH / 2, ...domainSectors.map((sector) => sector.outerRadius));
  const headings = [...(domainLabelLayout?.labelsById?.values() || [])];
  const halfWidth = Math.max(320, extent + PADDING, ...headings.map((label) => Math.abs(label.x) + label.width / 2 + PADDING));
  const halfHeight = Math.max(280, extent + PADDING, ...headings.map((label) => Math.abs(label.y) + label.height / 2 + PADDING));
  const width = Math.ceil(halfWidth * 2);
  const height = Math.ceil(halfHeight * 2);
  const centerX = width / 2;
  const centerY = height / 2;
  const positions = new Map([...relativePositions].map(([id, point]) => [id, { x: centerX + point.x, y: centerY + point.y }]));
  return { positions, width, height, centerX, centerY, radii, outerRadii: [...radii], outerRadius, domainSectors, domainLabelLayout, branchSectors };
}

/** Connect card edges rather than drawing relationship lines beneath their centers. */
export function futuresWheelEdgePath(start, end, startOrder = 1, endOrder = 1) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const distance = Math.hypot(dx, dy) || 1;
  const ux = dx / distance;
  const uy = dy / distance;
  const extent = (x, y, size) => Math.min(
    (size.width / 2) / Math.max(Math.abs(x), 0.0001),
    (size.height / 2) / Math.max(Math.abs(y), 0.0001),
  );
  const from = extent(ux, uy, wheelNodeSize(startOrder));
  const to = extent(ux, uy, wheelNodeSize(endOrder));
  const startX = start.x + ux * from;
  const startY = start.y + uy * from;
  const endX = end.x - ux * to;
  const endY = end.y - uy * to;
  return `M ${startX} ${startY} L ${endX} ${endY}`;
}
